import Card from '../components/card/Card';
import { saveClientInfo } from '../services/ClientService';
import {
  applicationInfo,
  clientData,
  documentData,
  signersData,
} from '../types/interfaces';
import BasicData from '../organisms/Origination/BasicData';
import Documents from '../organisms/Origination/Documents';
import { useEffect, useState } from 'react';
import H from '../components/texts/h/H';
import Label from '../components/texts/label/Label';
import { useLocation, useParams } from 'react-router-dom';
import Application from '../organisms/Origination/Application';
import { Toast, useToast } from '../components/toast/toast';
import Icon from '../components/icon/Icon';
import { capitalize, hasData } from '../utils/Utils';
import { getInitialClientData } from '../utils/Constants';
import Summary from '../organisms/Origination/Summary/Summary';
import {
  getApplication,
  sendApplication,
} from '../services/ApplicationService';
import Step from '../components/Step/Step';
import useStatus from '../hooks/useStatus';
import useStepNavigation from '../hooks/useStepNavigation';
import { useSignersContext } from '../context/SignersContext';
import { getApplicationStatusKey } from '../utils/applicationStatusUtils';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import theme from '../styles/theme';
import type { DetailLocationState } from '../utils/listNavigationState';

const hasNewDataFromServer = (
  serverData: clientData,
  localData: clientData
): boolean => {
  const hasMoreCorporateMembers =
    (serverData.corporate_structure?.length || 0) >
    (localData.corporate_structure?.length || 0);

  const hasOfficerMissing =
    !!serverData.officer && !localData.officer;

  const hasReferrerMissing =
    !!serverData.referrer_name && !localData.referrer_name;

  return hasMoreCorporateMembers || hasOfficerMissing || hasReferrerMissing;
};

const Origination = () => {
  const { cuit, id } = useParams();
  const fiscalId = useFiscalIdConfig();
  const { state } = useLocation();
  const detailState = (state ?? {}) as DetailLocationState;
  const status = detailState.status;
  const statusCode = detailState.statusCode;
  const fromSolicitudesTab = detailState.fromSolicitudesTab;
  const applicationStatusKey = getApplicationStatusKey(
    statusCode ?? status ?? ''
  );
  const {
    isRejected,
    isApproved,
    isFinished,
    isInCommittee,
    isInInstrumentation,
  } = useStatus({
    applicationStatus: applicationStatusKey,
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isValidatedClient, setIsValidatedClient] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState(state ? 4 : 1);
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(getInitialClientData);
  const [previousStep, setPreviousStep] = useState<number>(currentStep);
  const { savedSigners, setSavedSigners } = useSignersContext();
  const [applicationExists, setApplicationExists] = useState(null);

  // Hook para manejar la navegación automática de pasos
  const {
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation({
    cuit,
    id,
    moduleName: 'origination',
    totalSteps: 4,
    currentStep,
    setCurrentStep,
  });

  const mapCorporateToSigners = (corporateList: any[]): signersData[] => {
    return corporateList.map((member) => ({
      cuit: member.cuit || member.ci || '',
      name: member.name || member.first_name || '',
      email: member.email || '',
      asset_file_path: '',
      signer_type_code: member.signer_type_code || ['001'],
      role: member.position || '',
      position: member.position || '',
      locality: '',
      address: '',
    }));
  };
  useEffect(() => {
    (async () => {
      if (cuit && id) {
        const response = await getApplication(cuit, id);
        if (hasData(response)) {
          setClientData((prev) =>
            hasNewDataFromServer(response.data, prev) ? response.data : prev
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuit, id]);

  // Caso 1: solo mostrar mensaje cuando hay solicitud vigente (id_application), no cuando es null/undefined
  useEffect(() => {
    const hasActiveApplication =
      applicationExists != null &&
      applicationExists !== '' &&
      String(applicationExists) !== '-1';
    if (hasActiveApplication) {
      useToast.success({
        title: 'Ya existe una solicitud con esta empresa.',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
  }, [applicationExists]);

  // Resetear isClientDataLoaded cuando el usuario navega al paso 4
  // Esto asegura que siempre se haga un fetch fresco de los datos
  useEffect(() => {
    if (currentStep === 4) {
      setIsClientDataLoaded(false);
    }
  }, [currentStep]);

  const steps = [
    {
      id: 1,
      value: 'Datos del cliente',
      element: (
        <BasicData
          onSave={(e: any) => handleSave(e)}
          onSendStatus={(e: any) => {
            setApplicationExists(e);
          }}
          isSaving={isSaving}
          basicData={clientData}
          applicationStatus={status}
          isValidatedClient={(verify: boolean) => verifyClientData(verify)}
          currentStep={currentStep}
          previousStep={previousStep ?? currentStep}
          onDataChange={(updatedClientInfo: clientData) => {
            setClientData(updatedClientInfo);
          }}
          products={clientData.products}
          onProductsUpdated={(products) => {
            setClientData((prev) => ({ ...prev, products }));
          }}
        />
      ),
    },
    {
      id: 2,
      value: 'Carga de documentos',
      element: (
        <Documents
          onSave={(e: Array<documentData>) => handleSave(e)}
          documents={clientData.files}
          selectedLegalEntity={clientData.basic_data.legal_entity}
          cuit={clientData.cuit}
          applicationId={id}
          onPreviousClick={() => handleGoToPreviousStep()}
          isSaving={isSaving}
          applicationStatus={status}
          lufeBalanceSourceYear={clientData.lufe_balance_source_year}
        />
      ),
    },
    {
      id: 3,
      value: 'Solicitud',
      element: (
        <Application
          onSave={(e: Array<applicationInfo>) => handleSave(e)}
          applications={clientData.products}
          cuit={clientData.cuit}
          id={id}
          applicationStatus={applicationStatusKey}
          onPreviousClick={() => handleGoToPreviousStep()}
          isSaving={isSaving}
          manualTotalAmounts={clientData.manual_total_amount}
          requestedAmounts={clientData.requested_amount}
          reason={clientData.reason}
          review={clientData.review}
          extraComment={clientData.extra_comments}
          clientData={clientData}
          signers={savedSigners}
          isSyndicated={!!clientData.is_syndicated}
        />
      ),
    },
    {
      id: 4,
      value: 'Resumen',
      element: (
        <Summary
          setClientData={(e: any) => {
            if (e.signers?.length === 0 && e.corporate_structure?.length > 0) {
              setSavedSigners(
                mapCorporateToSigners(e.corporate_structure) as signersData[]
              );
            }
            if (
              e.signers?.length === 0 &&
              e.corporate_structure?.length === 0
            ) {
              setSavedSigners([]);
            }
            if (e.signers?.length > 0) {
              setSavedSigners(e.signers as signersData[]);
            }
            setClientData(e);
          }}
          applicationStatus={applicationStatusKey}
          onPreviousClick={
            fromSolicitudesTab ? undefined : () => goToPreviousStep()
          }
          isSaving={isSaving}
          clientData={clientData}
          onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
          isClientDataLoaded={isClientDataLoaded}
          showButtons={true}
          showPartners={true}
          showSummaryDocuments={true}
          showValidateComposition={!fromSolicitudesTab}
          showSummaryApplication={true}
        />
      ),
    },
  ];

  const handleStep1 = (dataToAdd: any, cuit: string) => {
    if (
      dataToAdd.clientInfo.signers.length === 0 &&
      dataToAdd.clientInfo.corporate_structure.length > 0
    ) {
      setSavedSigners(
        mapCorporateToSigners(dataToAdd.clientInfo.corporate_structure)
      );
    }
    if (
      dataToAdd.clientInfo.signers.length === 0 &&
      dataToAdd.clientInfo.corporate_structure.length === 0
    ) {
      setSavedSigners([]);
    }
    if (dataToAdd.clientInfo.signers.length > 0) {
      setSavedSigners(dataToAdd.clientInfo.signers);
    }
    setClientData(dataToAdd.clientInfo);

    // Siempre guardar los cambios, incluso si la aplicación está en revisión
    saveClientInfo(dataToAdd.clientInfo, cuit || '', parseInt(id || '0')).then(
      (response) => {
        if (!isRejected && !isApproved && !isFinished) {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        } else {
          goToNextStep();
          updateMaxStepReached(currentStep + 1);
          setIsSaving(false);
        }
      }
    );
  };

  const handleStep2 = (dataToAdd: any, cuit: string) => {
    const updatedClientData = { ...clientData };
    if (id) {
      updatedClientData.application_files = dataToAdd.files.map((file: documentData) => ({
        file_type: file.type,
        asset_file_path: file.file_name,
        is_basic_data: file.is_basic_data,
      }));
      updatedClientData.files = dataToAdd.files;
    } else {
      updatedClientData.files = dataToAdd.files;
    }
    if (dataToAdd.lufeBalanceSourceYear != null) {
      updatedClientData.lufe_balance_source_year = dataToAdd.lufeBalanceSourceYear;
    }
    setClientData(updatedClientData);

    if (!isRejected && !isApproved && !isFinished) {
      saveClientInfo(updatedClientData, cuit || '', parseInt(id || '0')).then(
        (response) => {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        }
      );
    } else {
      goToNextStep();
      updateMaxStepReached(currentStep + 1);
      setIsSaving(false);
    }
  };

  const handleStep3 = (dataToAdd: any, cuit: string) => {
    if (
      !isRejected &&
      !isApproved &&
      !isFinished &&
      !isInCommittee &&
      !isInInstrumentation
    ) {
      const updatedClientData = {
        ...clientData,
        products: dataToAdd.applications,
        manual_total_amount: dataToAdd.manualTotalAmounts,
        requested_amount: dataToAdd.requestedAmounts,
        reason: dataToAdd.reason,
        review: dataToAdd.review,
        extra_comments: dataToAdd.extraComment,
        corporate_structure: dataToAdd.corporateStructure,
        signers: dataToAdd.signers,
        effective_date: dataToAdd.effective_date,
        credit_type_id: dataToAdd.credit_type_id,
        credit_type_name: dataToAdd.credit_type_name,
        is_syndicated: !!dataToAdd.is_syndicated,
      };
      setClientData(updatedClientData);
      if (id?.length && id?.length > 0) {
        saveClientInfo(updatedClientData, cuit, parseInt(id || '0')).then((response) => {
          handleNextStep(response.status, dataToAdd.shouldContinue);
        });

        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length > 0
        ) {
          setSavedSigners(mapCorporateToSigners(dataToAdd.corporate_structure));
        }
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length === 0
        ) {
          setSavedSigners([]);
        }
        if (dataToAdd.signers?.length > 0) {
          setSavedSigners(dataToAdd.signers);
        }
      } else {
        saveClientInfo(updatedClientData, cuit, parseInt(id || '0')).then(
          (response) => {
            handleNextStep(response.status, dataToAdd.shouldContinue);
          }
        );
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length > 0
        ) {
          setSavedSigners(mapCorporateToSigners(dataToAdd.corporate_structure));
        }
        if (
          dataToAdd.signers?.length === 0 &&
          dataToAdd.corporate_structure?.length === 0
        ) {
          setSavedSigners([]);
        }
        if (dataToAdd.signers?.length > 0) {
          setSavedSigners(dataToAdd.signers);
        }
      }
    } else {
      goToNextStep();
      updateMaxStepReached(currentStep + 1);
      setIsSaving(false);
    }
  };

  const handleSave = (dataToAdd: any) => {
    setIsSaving(true);
    const cuit: string = dataToAdd.clientInfo?.cuit ?? clientData.cuit;
    switch (currentStep) {
      case 1:
        handleStep1(dataToAdd, cuit);
        break;
      case 2:
        handleStep2(dataToAdd, cuit);
        break;
      case 3:
        handleStep3(dataToAdd, cuit);
        break;
    }
  };

  const handleNextStep = (
    status: number | undefined,
    shouldContinue: boolean
  ) => {
    if (status === 200) {
      useToast.success({
        title: 'Guardado con éxito',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      if (shouldContinue) {
        goToNextStep();
        updateMaxStepReached(currentStep + 1);
      }
    } else {
      useToast.error({
        title: 'Ocurrió un error al guardar la información.',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
    setIsSaving(false);
  };
  const verifyClientData = (isVerified: boolean) => {
    setIsValidatedClient(isVerified);
  };

  const changeStep = (step: number) => {
    if (currentStep > step) {
      setPreviousStep(currentStep);
      navigateToStep(step);
    }
  };

  const handleGoToPreviousStep = () => {
    setPreviousStep(currentStep);
    goToPreviousStep();
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id='save-toast' />
      <Card display='flex' margin={{ left: '2' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {clientData.cuit
            ? 'Solicitud de ' +
              capitalize(clientData.basic_data.company_name) +
              ' ( ' +
              fiscalId.format(clientData.cuit) +
              ' )'
            : 'Alta de nueva carpeta'}
        </H>
      </Card>
      {(clientData.last_comment || !isValidatedClient) && (
        <Card
          display='flex'
          margin={{ top: '5' }}
          spacing={'3'}
          variant='warning'
          modifier='weak'
          gap='8px'
          borderRadiusAmount='4'
        >
          <Icon
            name='warning_triangle_outline'
            variant='warning'
            width={32}
            height={32}
          />
          <Card display='grid'>
            {!isValidatedClient && (
              <Card display='flex' margin={{ top: '2' }}>
                <Label type='1' variant='neutral' weight='bold'>
                  &bull;&nbsp;
                </Label>
                <Label type='1' variant='neutral' weight='bold'>
                  No se pudieron validar los datos:&nbsp;{' '}
                </Label>
                <Label type='1' variant='neutral'>
                  {' '}
                  Cargalos manualmente e intentá nuevamente
                </Label>
              </Card>
            )}
            {clientData.last_comment && (
              <Card display='flex' margin={{ top: '2' }}>
                {!isValidatedClient && (
                  <Label type='1' variant='neutral' weight='bold'>
                    &bull;&nbsp;
                  </Label>
                )}
                <Label type='1' variant='neutral' weight='bold'>
                  Cambios solicitados:&nbsp;
                </Label>
                <Label type='1' variant='neutral'>
                  {clientData.last_comment}
                </Label>
              </Card>
            )}
          </Card>
        </Card>
      )}
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '4', bottom: '4' }}
        width='100%'
      >
        <div
          style={{
            width: 'min(600px, 88%)',
            maxWidth: 600,
            position: 'relative',
            paddingTop: 4,
            paddingBottom: 0,
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: '10%',
              right: '10%',
              top: 20,
              height: 1,
              background: theme.colors.neutral.border.weaker,
              pointerEvents: 'none',
            }}
          />
          <div style={{ display: 'flex', width: '100%', gap: 0, position: 'relative' }}>
            {steps.map((step, index) => (
              <Step
                key={step.id}
                widthContainer='auto'
                widthCircle1='0'
                widthId='32px'
                heightId='32px'
                widthCircle2='0'
                step={step}
                stepsLength={steps.length}
                index={index}
                currentStep={currentStep}
                changeStep={(step) => changeStep(step)}
                atlanticStyle
              />
            ))}
          </div>
        </div>
      </Card>
      {steps[currentStep - 1].element}
    </Card>
  );
};

export default Origination;
